if (!customElements.get('gallery-variant-filter')) {
  customElements.define('gallery-variant-filter', class extends HTMLElement {
    connectedCallback() {
      const dataEl = document.getElementById(this.dataset.gvf);
      if (!dataEl) return;

      this._data = JSON.parse(dataEl.textContent);
      this._container = this.closest('[data-section-type]');
      this._images = this._container.querySelector('product-images');
      if (!this._images) return;

      this._buildGroups();
      this._applyInitial();

      this._onVariantChange = (e) => {
        const variant = e.detail?.variant;
        if (!variant) return;
        this._filterByVariant(variant);
      };
      this._container.addEventListener('theme:variant:change', this._onVariantChange);

      // Intercept navigation targeting a hidden slide before product-images processes it.
      // Determines direction from active→target index comparison and wraps within visible slides.
      this._onMediaSelect = (e) => {
        if (e._gvfHandled) return;
        const targetId = e.detail?.id;
        if (!targetId) return;
        const target = this._images.querySelector(`[data-media-id="${targetId}"]`);
        if (!target || !target.classList.contains('gallery--variant-hidden')) return;

        e.stopImmediatePropagation();

        const allSlides = Array.from(this._images.querySelectorAll('[data-slide]'));
        const visibleSlides = allSlides.filter(s => !s.classList.contains('gallery--variant-hidden'));
        if (!visibleSlides.length) return;

        const activeSlide = this._images.querySelector('.media--active');
        const activeIdx = allSlides.indexOf(activeSlide);
        const targetIdx = allSlides.indexOf(target);

        // Determine direction: forward if target is after active (mod-aware for wrap)
        const forwardDist = ((targetIdx - activeIdx) + allSlides.length) % allSlides.length;
        const goingForward = forwardDist <= allSlides.length / 2;

        // Wrap within the visible slides of the current group
        const redirect = goingForward
          ? visibleSlides[0]
          : visibleSlides[visibleSlides.length - 1];

        const redirectEvt = new CustomEvent('theme:media:select', {
          bubbles: true,
          detail: { id: redirect.dataset.mediaId },
        });
        redirectEvt._gvfHandled = true;
        redirect.dispatchEvent(redirectEvt);
      };
      this._images.addEventListener('theme:media:select', this._onMediaSelect, true);
    }

    disconnectedCallback() {
      this._container?.removeEventListener('theme:variant:change', this._onVariantChange);
      this._images?.removeEventListener('theme:media:select', this._onMediaSelect, true);
    }

    // Build a map: variantId -> Set of mediaIds that belong to that variant's image group.
    // Strategy: each variant's featured_media marks the start of its image group.
    // Images between two consecutive featured_media positions belong to the first variant's group.
    // Variants sharing the same featured_media are treated as a single group.
    _buildGroups() {
      const allMediaIds = this._data.m; // ordered list of all media IDs (strings)
      const variants = this._data.v;   // [{ id, fm }] where fm = featured_media id or null

      // Collect unique featured_media positions (index in allMediaIds).
      // We only create groups for variants that have a featured_media assigned.
      const fmToVariants = new Map();
      for (const v of variants) {
        if (v.fm == null) continue;
        const idx = allMediaIds.indexOf(String(v.fm));
        if (idx < 0) continue;
        if (!fmToVariants.has(idx)) fmToVariants.set(idx, []);
        fmToVariants.get(idx).push(v.id);
      }

      if (fmToVariants.size === 0) {
        this._groups = null;
        return;
      }

      // Sort start positions
      const startPositions = [...fmToVariants.keys()].sort((a, b) => a - b);

      // Build groups: from each start to the next start (or end of array)
      this._groups = new Map(); // variantId -> Set<mediaId>
      for (let i = 0; i < startPositions.length; i++) {
        const from = startPositions[i];
        const to = startPositions[i + 1] ?? allMediaIds.length;
        const mediaSet = new Set(allMediaIds.slice(from, to).map(String));
        for (const vid of fmToVariants.get(from)) {
          this._groups.set(vid, mediaSet);
        }
      }

      // The union of all grouped media IDs (used to decide which slides to touch)
      this._allGrouped = new Set([...this._groups.values()].flatMap(s => [...s]));
    }

    _applyInitial() {
      const currentVariantId = this._data.cv;
      if (currentVariantId != null) {
        this._applyFilter(currentVariantId);
      }
    }

    _filterByVariant(variant) {
      this._images.classList.add('gvf--no-transition');
      this._applyFilter(variant.id);
      // One rAF lets the browser apply the class; the next lets layout settle before removing it
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this._images.classList.remove('gvf--no-transition');
      }));
    }

    _applyFilter(variantId) {
      if (!this._groups) return; // no variant-specific images — nothing to filter

      const group = this._groups.get(variantId);

      // Show everything if this variant has no assigned group
      if (!group) {
        this._showAll();
        return;
      }

      const slides = this._images.querySelectorAll('[data-slide]');
      slides.forEach(slide => {
        const imageId = String(slide.dataset.imageId);
        // Only hide/show slides that belong to any group — ungrouped images always stay visible
        if (!this._allGrouped.has(imageId)) return;
        const hide = !group.has(imageId);
        slide.classList.toggle('gallery--variant-hidden', hide);
      });

      this._syncThumbs();
      this._ensureActiveSlideVisible();
    }

    _showAll() {
      this._images.querySelectorAll('.gallery--variant-hidden').forEach(el => {
        el.classList.remove('gallery--variant-hidden');
      });
      this._syncThumbs();
    }

    // Mirror slide visibility onto thumbnail items
    _syncThumbs() {
      const thumbItems = this._container.querySelectorAll('[data-thumb-item]');
      thumbItems.forEach(thumb => {
        const link = thumb.querySelector('[data-media-id]');
        if (!link) return;
        const slide = this._images.querySelector(`[data-media-id="${link.dataset.mediaId}"]`);
        if (!slide) return;
        thumb.classList.toggle('gallery--variant-hidden', slide.classList.contains('gallery--variant-hidden'));
      });
    }

    // If the currently active slide is now hidden, switch to the first visible one
    _ensureActiveSlideVisible() {
      const active = this._images.querySelector('.media--active');
      if (!active || !active.classList.contains('gallery--variant-hidden')) return;

      const first = this._images.querySelector('[data-slide]:not(.gallery--variant-hidden)');
      if (!first) return;

      first.dispatchEvent(new CustomEvent('theme:media:select', {
        bubbles: true,
        detail: { id: first.dataset.mediaId },
      }));
    }
  });
}
